import { useCallback, useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, MoreOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Col, Dropdown, Form, Input, Menu, Popconfirm, Row, Select, Space, Table, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import {
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  NEW_TNA_TEMPLATE_BASE,
  NEW_TNA_TEMPLATE_UPDATE_DELETE_BASE,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import ListTable from "common/ListTable";
import { hasPermission, isArrayAndHasValue } from "utils/functions";
import {
  NO_PERMISSION_REQUIRED,
  TNA_TEMPLATE_CREATE_PERMISSION,
  TNA_TEMPLATE_DELETE_PERMISSION,
  TNA_TEMPLATE_EDIT_PERMISSION,
} from "routes/permissions";
import UploadTemplateModal from "./UploadTemplateModal";

const NewTNATemplate = () => {
  const navigate = useNavigate();
  const { Title } = Typography;
  const { Option } = Select;

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyerList, setBuyerList] = useState([]);
  const [buyingOfficeList, setBuyingOfficeList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const [filterForm] = Form.useForm();

  const getBuyerList = async (name) => {
    const response = await getData(GET_BUYER_ENDPOINT, false, {
      per_page: 50,
      ...(name ? { name } : {}),
    });

    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
  };

  const getBuyingOfficeList = async (name) => {
    const response = await getData(GET_BUYING_OFFICE_ENDPOINT, false, {
      per_page: 50,
      ...(name ? { name } : {}),
    });

    if (response?.data?.code === 200) {
      setBuyingOfficeList(response?.data?.data?.data || []);
    }
  };

  const getAllTemplates = useCallback(async () => {
    const values = filterForm.getFieldsValue();

    setLoading(true);
    setFetchError(null);
    const response = await getData(NEW_TNA_TEMPLATE_BASE, false, {
      per_page: 1000,
      ...(values?.title ? { title: values?.title } : {}),
      ...(values?.buyer_id ? { buyer_id: values?.buyer_id } : {}),
      ...(values?.buying_office_id ? { buying_office_id: values?.buying_office_id } : {}),
    });

    setLoading(false);

    if (response?.data?.code === 200) {
      const rows = response?.data?.data?.data || [];
      const sortedRows = [...rows].sort((a, b) => {
        const dateA = new Date(a?.created_at || 0).getTime();
        const dateB = new Date(b?.created_at || 0).getTime();
        return dateB - dateA;
      });
      setTableData(sortedRows);
      return;
    }

    setTableData([]);
    setFetchError("Could not load this list.");
  }, [filterForm]);

  useEffect(() => {
    getBuyerList();
    getBuyingOfficeList();
  }, []);

  useEffect(() => {
    getAllTemplates();
  }, [getAllTemplates, refresh]);

  const handleDelete = async (id) => {
    const response = await deleteData(`${NEW_TNA_TEMPLATE_UPDATE_DELETE_BASE}/${id}`);

    if (response) {
      HandleNotification("success", "bottomRight", "TNA Template deleted successfully", null);
      getAllTemplates();
      return;
    }

    HandleNotification("error", "bottomRight", "Something went wrong", null);
  };

  const actionMenu = (record) => (
    <Menu
      items={[
        {
          key: "edit",
          label: (
            <Link to={`/tanda/update-new-template/${record?.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          disabled: !hasPermission([TNA_TEMPLATE_EDIT_PERMISSION, NO_PERMISSION_REQUIRED]),
        },
        {
          key: "delete",
          label: (
            <Popconfirm
              title="Are you sure you want to delete this template?"
              onConfirm={() => handleDelete(record?.id)}
            >
              <span style={{ color: "red" }}>
                <DeleteOutlined /> Delete
              </span>
            </Popconfirm>
          ),
          disabled: !hasPermission([TNA_TEMPLATE_DELETE_PERMISSION, NO_PERMISSION_REQUIRED]),
        },
      ]}
    />
  );

  const columns = [
    {
      title: "Title",
      dataIndex: ["title"],
      key: "title",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer", "name"],
      key: "buyer",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office", "name"],
      key: "buying_office",
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown overlay={actionMenu(record)}>
          <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const onFilterReset = () => {
    filterForm.resetFields();
    setRefresh((prev) => prev + 1);
  };

  return (
    <AppContent
      header={
        <AppPageHeader
          title={<Title level={5}>TNA Template</Title>}
          extra={
            <Space>
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={() => navigate("/tanda/add-new-template")}
                disabled={!hasPermission([TNA_TEMPLATE_CREATE_PERMISSION])}
                addButtonText={hasPermission([TNA_TEMPLATE_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
              {hasPermission([TNA_TEMPLATE_CREATE_PERMISSION]) && (
                <Button size="small" icon={<UploadOutlined />} onClick={() => setIsUploadOpen(true)}>
                  Upload Template
                </Button>
              )}
            </Space>
          }
        />
      }
      content={
        <>
          {showFilter && (
            <Card style={{ marginBottom: 12 }}>
              <Form form={filterForm} layout="vertical" onFinish={getAllTemplates}>
                <Row gutter={8}>
                  <Col span={8}>
                    <Form.Item name="title" label="Title">
                      <Input size="small" placeholder="Filter by title" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="buyer_id" label="Buyer">
                      <Select
                        size="small"
                        showSearch
                        allowClear
                        placeholder="Select Buyer"
                        optionFilterProp="children"
                        onSearch={getBuyerList}
                      >
                        {isArrayAndHasValue(buyerList) &&
                          buyerList.map((item) => (
                            <Option key={item?.id} value={item?.id}>
                              {item?.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="buying_office_id" label="Buying Office">
                      <Select
                        size="small"
                        showSearch
                        allowClear
                        placeholder="Select Buying Office"
                        optionFilterProp="children"
                        onSearch={getBuyingOfficeList}
                      >
                        {isArrayAndHasValue(buyingOfficeList) &&
                          buyingOfficeList.map((item) => (
                            <Option key={item?.id} value={item?.id}>
                              {item?.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={8}>
                  <Col>
                    <Button type="primary" size="small" htmlType="submit" icon={<SearchOutlined />}>
                      Search
                    </Button>
                  </Col>
                  <Col>
                    <Button size="small" onClick={onFilterReset}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}

          <Table rowKey="id" loading={loading} dataSource={tableData} columns={columns} pagination={false} />

          <UploadTemplateModal
            open={isUploadOpen}
            onClose={() => setIsUploadOpen(false)}
            onUploaded={() => {
              setIsUploadOpen(false);
              setRefresh((prev) => prev + 1);
            }}
          />
        </>
      }
    />
  );
};

export default NewTNATemplate;
